import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Heading from 'components/Heading/Heading';
import { useGetBookingDetailsQuery } from 'application/features/bookings/bookingApiSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { BookingInvoiceData } from 'data/types/booking';
import { companyDetails } from 'data/company_details';

const InvoiceDetailsPage: React.FC = () => {
    const { t, ready } = useTranslation("translation");
    const navigate = useNavigate();

    const { bookingId } = useParams<{ bookingId: string }>();
    const { data: booking, isLoading, isError, isSuccess} = useGetBookingDetailsQuery(bookingId || '');
    
    if (!ready) {
        return null;
    }

    if (isError) {

        navigate('/host/bookings');
        return null;

    } else if (isLoading) {

        return (
            <div>{t("translation:invoicePage.loading") || "Loading invoice details..."}</div>
        );

    } else if (isSuccess && booking) {
        
        const invoiceData = booking as BookingInvoiceData;

        return (
            <div className="container relative pt-5 pb-16 lg:pb-20 lg:pt-8 flex flex-col">
                <Helmet>
                    <title>{t("translation:invoicePage.pageTitle") || "Invoice Details"}</title>
                </Helmet>

                <Heading
                    desc={t("translation:invoicePage.invoiceDetailsDescription") || "Invoice details for the booking"}
                    className="mb-8"
                >
                    {t("translation:invoicePage.invoiceDetailsTitle") || "Invoice Details"}
                </Heading>

                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {t("translation:invoicePage.booking") || "Booking"} #{invoiceData.booking_uuid}
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            {t("translation:invoicePage.status") || "Status"}: {invoiceData.status}
                        </p>
                    </div>
                    <div className="border-t border-gray-200">
                        <dl>
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">
                                    {t("translation:invoicePage.bookingDetails.sectionTitle") || "Booking Details"}
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <p>{t("translation:invoicePage.bookingDetails.checkIn") || "Check-in"}: {new Date(invoiceData.check_in).toLocaleDateString('it-IT')}</p>
                                    <p>{t("translation:invoicePage.bookingDetails.checkOut") || "Check-out"}: {new Date(invoiceData.check_out).toLocaleDateString('it-IT')}</p>
                                    <p>{t("translation:invoicePage.bookingDetails.guests") || "Guests"}: {invoiceData.guests}</p>
                                    <p>{t("translation:invoicePage.bookingDetails.price") || "Price"}: €{invoiceData.price}</p>
                                </dd>
                            </div>

                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">
                                    {t("translation:invoicePage.propertyDetails.sectionTitle") || "Property Details"}
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <p>{t("translation:invoicePage.propertyDetails.propertyName") || "Property Name"}: {invoiceData.property_name}</p>
                                    <p>{t("translation:invoicePage.propertyDetails.propertyAddress") || "Property Address"}: {invoiceData.property_address}</p>
                                </dd>
                            </div>

                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">
                                    {t("translation:invoicePage.clientDetails.sectionTitle") || "Client Details"}
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <p>{t("translation:invoicePage.clientDetails.fullName") || "Full Name"}: {invoiceData.client_name}</p>
                                    <p>{t("translation:invoicePage.clientDetails.email") || "Email"}: {invoiceData.client_email}</p>
                                    <p>{t("translation:invoicePage.clientDetails.phone") || "Phone"}: {invoiceData.client_phone}</p>
                                </dd>
                            </div>

                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">
                                    {t("translation:invoicePage.billingDetails.sectionTitle") || "Billing Details"}
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <p>{t("translation:invoicePage.billingDetails.companyName") || "Company Name"}: {invoiceData.client_company_name}</p>
                                    <p>{t("translation:invoicePage.billingDetails.vatNumber") || "VAT Number"}: {invoiceData.client_vat_number}</p>
                                    <div>
                                        <p>{t("translation:invoicePage.billingDetails.legalAddress") || "Legal Address"}:</p> 
                                        <ul className="ml-4">
                                            {invoiceData.client_legal_address && <li>{invoiceData.client_legal_address},</li>}
                                            {invoiceData.client_legal_city && <li>{invoiceData.client_legal_city},</li>} 
                                            {invoiceData.client_legal_postal_code && <li>{invoiceData.client_legal_postal_code}</li>}
                                        </ul>
                                    </div>
                                    <p>{t("translation:invoicePage.billingDetails.uniqueInvoiceCode") || "Unique Invoice Code"}: {invoiceData.client_unique_invoice_code}</p>
                                    <p>{t("translation:invoicePage.billingDetails.fiscalCode") || "Company Registration Number"}: {invoiceData.client_fiscal_code}</p>
                                </dd>
                            </div>

                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">
                                    {t("translation:invoicePage.issuerDetails.sectionTitle") || "Issuer Details"}
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <p>{t("translation:invoicePage.issuerDetails.companyName") || "Company Name"}: {companyDetails.company_name}</p>
                                    <p>{t("translation:invoicePage.issuerDetails.vatNumber") || "VAT Number"}: {companyDetails.vat_number || 'N/A'}</p>
                                    <div>
                                        <p>{t("translation:invoicePage.issuerDetails.legalAddress") || "Legal Address"}:</p> 
                                        <ul className="ml-4">
                                            <li>{companyDetails.legal_address},</li>
                                            <li>{companyDetails.legal_city},</li> 
                                            <li>{companyDetails.legal_postal_code}</li>
                                        </ul>
                                    </div>
                                    <p>{t("translation:invoicePage.issuerDetails.uniqueCode") || "Unique Code"}: {companyDetails.unique_code}</p>
                                    <p>{t("translation:invoicePage.issuerDetails.fiscalCode") || "Fiscal Code"}: {companyDetails.fiscal_code}</p>
                                </dd>
                            </div>

                        </dl>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default InvoiceDetailsPage; 